import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Context for draft template overrides used during live editing.
 * This allows BaseComponents to read draft values (like aspect ratio)
 * before falling back to saved settings in Redux.
 */
export interface DraftTemplateOverrides {
    /** Aspect ratio class like 'aspect_3-4' for card images */
    itemAspectRatio?: string | null;
    /** Card size like 'small', 'normal', 'large' */
    itemCardSize?: string | null;
    /** Whether the editor panel is minimized (in dock mode) */
    isMinimized?: boolean;
    /** Whether there are unsaved changes in the draft */
    hasUnsavedChanges?: boolean;
}

const DraftOverridesContext = createContext<DraftTemplateOverrides | null>(null);

export interface DraftOverridesProviderProps {
    children: ReactNode;
    value: DraftTemplateOverrides | null;
}

/**
 * Provides draft template overrides to components during live editing.
 * Wrap the preview components with this provider and pass draft values.
 */
export const DraftOverridesProvider = ({
    children,
    value,
}: DraftOverridesProviderProps) => {
    const memoizedValue = useMemo(() => value, [value]);
    return (
        <DraftOverridesContext.Provider value={memoizedValue}>
            {children}
        </DraftOverridesContext.Provider>
    );
};

/**
 * Hook to read draft template overrides.
 * Returns null if not within a DraftOverridesProvider (i.e., not in edit mode).
 */
export const useDraftOverrides = (): DraftTemplateOverrides | null => {
    return useContext(DraftOverridesContext);
};
